import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
  createDeviceMake,
  createDeviceModel,
  deleteDeviceMake,
  deleteDeviceModel,
  DEVICE_CATEGORIES,
  listDeviceMakes,
  listDeviceModels,
  updateDeviceMake,
  updateDeviceModel,
  type DeviceCategory,
  type DeviceMake,
  type DeviceModel,
} from '@/lib/api/devices.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

function categoryLabel(c: string | null | undefined): string {
  if (!c) return '—';
  return c.charAt(0).toUpperCase() + c.slice(1);
}

export function DeviceCatalogSettingsPage() {
  const queryClient = useQueryClient();
  const [makeSearch, setMakeSearch] = useState('');
  const [showInactiveMakes, setShowInactiveMakes] = useState(false);
  const [selectedMakeId, setSelectedMakeId] = useState<string | null>(null);
  const [modelSearch, setModelSearch] = useState('');
  const [modelCategory, setModelCategory] = useState('');
  const [showInactiveModels, setShowInactiveModels] = useState(false);

  const [addMakeOpen, setAddMakeOpen] = useState(false);
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [editMake, setEditMake] = useState<DeviceMake | null>(null);
  const [editModel, setEditModel] = useState<DeviceModel | null>(null);
  const [blockDialog, setBlockDialog] = useState<
    | {
        kind: 'make';
        make: DeviceMake;
        modelCount: number;
        ticketCount?: number;
        appointmentCount?: number;
        pricingCount?: number;
        inventoryCount?: number;
        templateCount?: number;
        warrantyCount?: number;
        inquiryCount?: number;
        quoteCount?: number;
      }
    | {
        kind: 'model';
        model: DeviceModel;
        ticketCount: number;
        appointmentCount?: number;
        pricingCount: number;
        inventoryCount: number;
        templateCount?: number;
        inquiryCount?: number;
        quoteCount?: number;
      }
    | null
  >(null);

  const makesQuery = useQuery({
    queryKey: ['device-makes-admin', makeSearch, showInactiveMakes],
    queryFn: () =>
      listDeviceMakes({
        search: makeSearch.trim() || undefined,
        includeInactive: showInactiveMakes,
        withCounts: true,
      }),
  });

  const makes = makesQuery.data ?? [];
  const selectedMake = makes.find((m) => m.id === selectedMakeId) ?? null;

  useEffect(() => {
    if (selectedMakeId && !makes.some((m) => m.id === selectedMakeId)) {
      setSelectedMakeId(null);
    }
  }, [makes, selectedMakeId]);

  const modelsQuery = useQuery({
    queryKey: [
      'device-models-admin',
      selectedMakeId,
      modelSearch,
      modelCategory,
      showInactiveModels,
    ],
    queryFn: () =>
      listDeviceModels({
        makeId: selectedMakeId!,
        search: modelSearch.trim() || undefined,
        category: modelCategory || undefined,
        includeInactive: showInactiveModels,
      }),
    enabled: Boolean(selectedMakeId),
  });

  const models = modelsQuery.data ?? [];

  function invalidateCatalog(makeId?: string) {
    void queryClient.invalidateQueries({ queryKey: ['device-makes'] });
    void queryClient.invalidateQueries({ queryKey: ['device-makes-admin'] });
    void queryClient.invalidateQueries({ queryKey: ['device-models'] });
    void queryClient.invalidateQueries({ queryKey: ['device-models-admin'] });
    if (makeId) {
      void queryClient.invalidateQueries({ queryKey: ['device-models-admin', makeId] });
    }
  }

  const deactivateMakeMut = useMutation({
    mutationFn: (make: DeviceMake) => updateDeviceMake(make.id, { active: false }),
    onSuccess: (_, make) => {
      toastSuccess(`${make.name} deactivated`);
      setBlockDialog(null);
      invalidateCatalog(make.id);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deactivateModelMut = useMutation({
    mutationFn: (model: DeviceModel) => updateDeviceModel(model.id, { active: false }),
    onSuccess: (_, model) => {
      toastSuccess(`${model.name} deactivated`);
      setBlockDialog(null);
      invalidateCatalog(model.makeId);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  async function handleDeleteMake(make: DeviceMake) {
    try {
      await deleteDeviceMake(make.id);
      toastSuccess(`${make.name} deleted`);
      if (selectedMakeId === make.id) setSelectedMakeId(null);
      invalidateCatalog(make.id);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data as {
          data?: {
            modelCount?: number;
            ticketCount?: number;
            appointmentCount?: number;
            pricingCount?: number;
            inventoryCount?: number;
            templateCount?: number;
            warrantyCount?: number;
            inquiryCount?: number;
            quoteCount?: number;
          };
        };
        setBlockDialog({
          kind: 'make',
          make,
          modelCount: data.data?.modelCount ?? make.modelCount ?? 0,
          ticketCount: data.data?.ticketCount ?? 0,
          appointmentCount: data.data?.appointmentCount ?? 0,
          pricingCount: data.data?.pricingCount ?? 0,
          inventoryCount: data.data?.inventoryCount ?? 0,
          templateCount: data.data?.templateCount ?? 0,
          warrantyCount: data.data?.warrantyCount ?? 0,
          inquiryCount: data.data?.inquiryCount ?? 0,
          quoteCount: data.data?.quoteCount ?? 0,
        });
        return;
      }
      toastError(parseApiError(error).message);
    }
  }

  async function handleDeleteModel(model: DeviceModel) {
    try {
      await deleteDeviceModel(model.id);
      toastSuccess(`${model.name} deleted`);
      invalidateCatalog(model.makeId);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data as {
          data?: {
            ticketCount?: number;
            appointmentCount?: number;
            pricingCount?: number;
            inventoryCount?: number;
            templateCount?: number;
            inquiryCount?: number;
            quoteCount?: number;
          };
        };
        setBlockDialog({
          kind: 'model',
          model,
          ticketCount: data.data?.ticketCount ?? 0,
          appointmentCount: data.data?.appointmentCount ?? 0,
          pricingCount: data.data?.pricingCount ?? 0,
          inventoryCount: data.data?.inventoryCount ?? 0,
          templateCount: data.data?.templateCount ?? 0,
          inquiryCount: data.data?.inquiryCount ?? 0,
          quoteCount: data.data?.quoteCount ?? 0,
        });
        return;
      }
      toastError(parseApiError(error).message);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Device Catalog</h2>
        <p className="mt-1 text-sm text-slate-500">
          Manage manufacturers and models used at intake. Deactivate to hide from new tickets
          without breaking history.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        {/* LEFT — Makes */}
        <section className="flex max-h-[70vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="space-y-3 border-b border-slate-200 p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm"
                placeholder="Search makes..."
                value={makeSearch}
                onChange={(e) => setMakeSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
              onClick={() => setAddMakeOpen(true)}
            >
              <Plus className="h-4 w-4" /> Add Make
            </button>
          </div>
          <ul className="flex-1 overflow-y-auto">
            {makes.map((make) => {
              const selected = make.id === selectedMakeId;
              const inactive = make.active === false;
              return (
                <li key={make.id}>
                  <div
                    className={cn(
                      'group flex cursor-pointer items-center gap-2 px-4 py-3 text-sm transition-colors',
                      selected ? 'bg-primary/10 text-primary' : 'hover:bg-slate-50',
                      inactive && 'opacity-60',
                    )}
                    onClick={() => setSelectedMakeId(make.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">
                        {make.name}
                        {inactive ? ' (inactive)' : ''}
                      </div>
                    </div>
                    <span className="tabular-nums text-slate-500">{make.modelCount ?? 0}</span>
                    <span className="text-slate-400">▸</span>
                    <div className="hidden gap-1 group-hover:flex">
                      <button
                        type="button"
                        className="rounded p-1 text-slate-500 hover:bg-white"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditMake(make);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-red-600 hover:bg-white"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeleteMake(make);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
            {!makesQuery.isLoading && makes.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-slate-500">No manufacturers found</li>
            ) : null}
          </ul>
          <label className="flex items-center gap-2 border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactiveMakes}
              onChange={(e) => setShowInactiveMakes(e.target.checked)}
            />
            Show inactive
          </label>
        </section>

        {/* RIGHT — Models */}
        <section className="flex max-h-[70vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {!selectedMake ? (
            <div className="flex flex-1 items-center justify-center p-10 text-sm text-slate-500">
              Select a manufacturer to view its models
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-lg font-bold text-slate-950">
                    {selectedMake.name}{' '}
                    <span className="font-normal text-slate-500">
                      —{' '}
                      {!modelSearch && !modelCategory && modelsQuery.isSuccess
                        ? models.length
                        : (selectedMake.modelCount ?? models.length)}{' '}
                      models
                    </span>
                  </h3>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => setAddModelOpen(true)}
                >
                  <Plus className="h-4 w-4" /> Add Model
                </button>
              </div>
              <div className="flex flex-wrap gap-2 border-b border-slate-100 px-4 py-3">
                <div className="relative min-w-[180px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm"
                    placeholder="Search models..."
                    value={modelSearch}
                    onChange={(e) => setModelSearch(e.target.value)}
                  />
                </div>
                <select
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={modelCategory}
                  onChange={(e) => setModelCategory(e.target.value)}
                >
                  <option value="">All categories</option>
                  {DEVICE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {categoryLabel(c)}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={showInactiveModels}
                    onChange={(e) => setShowInactiveModels(e.target.checked)}
                  />
                  Show inactive
                </label>
              </div>
              <div className="flex-1 overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Year</th>
                      <th className="px-4 py-3">Value</th>
                      <th className="px-4 py-3 text-right"> </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {models.map((model) => (
                      <ModelRow
                        key={model.id}
                        model={model}
                        canEdit
                        onSaved={() => invalidateCatalog(model.makeId)}
                        onEdit={() => setEditModel(model)}
                        onDeactivate={() => deactivateModelMut.mutate(model)}
                        onDelete={() => void handleDeleteModel(model)}
                      />
                    ))}
                    {!modelsQuery.isLoading && models.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                          No models for this manufacturer
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      {addMakeOpen ? (
        <MakeModal
          title="Add Make"
          onClose={() => setAddMakeOpen(false)}
          onSubmit={async (values) => {
            const created = await createDeviceMake(values);
            toastSuccess(`${created.name} added`);
            invalidateCatalog();
            setSelectedMakeId(created.id);
            setAddMakeOpen(false);
          }}
        />
      ) : null}

      {editMake ? (
        <MakeModal
          title="Edit Make"
          initial={editMake}
          onClose={() => setEditMake(null)}
          onSubmit={async (values) => {
            await updateDeviceMake(editMake.id, values);
            toastSuccess('Manufacturer updated');
            invalidateCatalog(editMake.id);
            setEditMake(null);
          }}
        />
      ) : null}

      {addModelOpen && selectedMake ? (
        <ModelModal
          title="Add Model"
          makes={makes}
          initialMakeId={selectedMake.id}
          allowAddAnother
          onClose={() => setAddModelOpen(false)}
          onSubmit={async (values, addAnother) => {
            const created = await createDeviceModel(values);
            toastSuccess(`${created.name} added`);
            invalidateCatalog(values.makeId);
            if (!addAnother) setAddModelOpen(false);
            return created;
          }}
        />
      ) : null}

      {editModel ? (
        <ModelModal
          title="Edit Model"
          makes={makes}
          initial={editModel}
          onClose={() => setEditModel(null)}
          onSubmit={async (values) => {
            await updateDeviceModel(editModel.id, values);
            toastSuccess('Model updated');
            invalidateCatalog(editModel.makeId);
            setEditModel(null);
          }}
        />
      ) : null}

      {blockDialog?.kind === 'make' ? (
        <BlockedDialog
          title={`Cannot delete ${blockDialog.make.name}`}
          onCancel={() => setBlockDialog(null)}
          onDeactivate={() => deactivateMakeMut.mutate(blockDialog.make)}
          deactivatePending={deactivateMakeMut.isPending}
        >
          <p className="font-medium">In use by:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            {(blockDialog.modelCount ?? 0) > 0 ? (
              <li>{blockDialog.modelCount} models</li>
            ) : null}
            {(blockDialog.ticketCount ?? 0) > 0 ? (
              <li>{blockDialog.ticketCount} tickets</li>
            ) : null}
            {(blockDialog.appointmentCount ?? 0) > 0 ? (
              <li>{blockDialog.appointmentCount} appointments</li>
            ) : null}
            {(blockDialog.pricingCount ?? 0) > 0 ? (
              <li>{blockDialog.pricingCount} pricing rules</li>
            ) : null}
            {(blockDialog.inventoryCount ?? 0) > 0 ? (
              <li>{blockDialog.inventoryCount} inventory items</li>
            ) : null}
            {(blockDialog.templateCount ?? 0) > 0 ? (
              <li>{blockDialog.templateCount} ticket templates</li>
            ) : null}
            {(blockDialog.warrantyCount ?? 0) > 0 ? (
              <li>{blockDialog.warrantyCount} warranty rules</li>
            ) : null}
            {(blockDialog.inquiryCount ?? 0) > 0 ? (
              <li>{blockDialog.inquiryCount} inquiries</li>
            ) : null}
            {(blockDialog.quoteCount ?? 0) > 0 ? (
              <li>{blockDialog.quoteCount} quotes</li>
            ) : null}
          </ul>
          <p className="mt-2">
            Deactivate it instead to hide it from new tickets while preserving history.
          </p>
        </BlockedDialog>
      ) : null}

      {blockDialog?.kind === 'model' ? (
        <BlockedDialog
          title={`Cannot delete ${blockDialog.model.name}`}
          onCancel={() => setBlockDialog(null)}
          onDeactivate={() => deactivateModelMut.mutate(blockDialog.model)}
          deactivatePending={deactivateModelMut.isPending}
        >
          <p className="font-medium">In use by:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            {(blockDialog.ticketCount ?? 0) > 0 ? (
              <li>{blockDialog.ticketCount} tickets</li>
            ) : null}
            {(blockDialog.appointmentCount ?? 0) > 0 ? (
              <li>{blockDialog.appointmentCount} appointments</li>
            ) : null}
            {(blockDialog.pricingCount ?? 0) > 0 ? (
              <li>{blockDialog.pricingCount} pricing rules</li>
            ) : null}
            {(blockDialog.inventoryCount ?? 0) > 0 ? (
              <li>{blockDialog.inventoryCount} inventory items</li>
            ) : null}
            {(blockDialog.templateCount ?? 0) > 0 ? (
              <li>{blockDialog.templateCount} ticket templates</li>
            ) : null}
            {(blockDialog.inquiryCount ?? 0) > 0 ? (
              <li>{blockDialog.inquiryCount} inquiries</li>
            ) : null}
            {(blockDialog.quoteCount ?? 0) > 0 ? (
              <li>{blockDialog.quoteCount} quotes</li>
            ) : null}
          </ul>
          <p className="mt-2">Deactivate instead to hide it from new tickets.</p>
        </BlockedDialog>
      ) : null}
    </div>
  );
}

function ModelRow({
  model,
  canEdit,
  onSaved,
  onEdit,
  onDeactivate,
  onDelete,
}: {
  model: DeviceModel;
  canEdit: boolean;
  onSaved: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [yearDraft, setYearDraft] = useState(model.releaseYear?.toString() ?? '');
  const [valueDraft, setValueDraft] = useState(model.estimatedValue ?? '');

  useEffect(() => {
    setYearDraft(model.releaseYear?.toString() ?? '');
    setValueDraft(model.estimatedValue ?? '');
  }, [model.id, model.releaseYear, model.estimatedValue]);

  async function saveField(patch: Parameters<typeof updateDeviceModel>[1]) {
    if (!canEdit) return;
    try {
      await updateDeviceModel(model.id, patch);
      onSaved();
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }

  return (
    <tr className={cn(model.active === false && 'opacity-60')}>
      <td className="px-4 py-3 font-semibold text-slate-900">
        {model.name}
        {model.active === false ? (
          <span className="ml-2 text-xs font-normal text-slate-500">(inactive)</span>
        ) : null}
      </td>
      <td className="px-4 py-3 text-slate-600">{categoryLabel(model.category)}</td>
      <td className="px-4 py-3">
        {canEdit ? (
          <input
            className="w-20 rounded-lg border border-transparent px-2 py-1 hover:border-slate-300 focus:border-primary focus:outline-none"
            value={yearDraft}
            placeholder="—"
            onChange={(e) => setYearDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onBlur={() => {
              const n = yearDraft ? Number(yearDraft) : null;
              if ((n ?? null) === (model.releaseYear ?? null)) return;
              void saveField({ releaseYear: n && n >= 1990 ? n : null });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
        ) : (
          model.releaseYear ?? '—'
        )}
      </td>
      <td className="px-4 py-3">
        {canEdit ? (
          <input
            className="w-24 rounded-lg border border-transparent px-2 py-1 hover:border-slate-300 focus:border-primary focus:outline-none"
            value={valueDraft}
            placeholder="—"
            onChange={(e) => setValueDraft(e.target.value)}
            onBlur={() => {
              const raw = valueDraft.trim();
              const next = raw === '' ? null : raw;
              if (String(next ?? '') === String(model.estimatedValue ?? '')) return;
              void saveField({ estimatedValue: next });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
        ) : model.estimatedValue != null ? (
          formatCurrency(model.estimatedValue)
        ) : (
          '—'
        )}
      </td>
      <td className="relative px-4 py-3 text-right">
        {canEdit ? (
          <>
            <button
              type="button"
              className="rounded-lg px-2 py-1 font-bold text-slate-500 hover:bg-slate-100"
              onClick={() => setMenuOpen((v) => !v)}
            >
              ⋮
            </button>
            {menuOpen ? (
              <div className="absolute right-4 z-20 mt-1 w-40 rounded-xl border border-slate-200 bg-white py-1 text-left shadow-lg">
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit();
                  }}
                >
                  Edit
                </button>
                {model.active !== false ? (
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => {
                      setMenuOpen(false);
                      onDeactivate();
                    }}
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => {
                      setMenuOpen(false);
                      void saveField({ active: true });
                    }}
                  >
                    Reactivate
                  </button>
                )}
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-red-700 hover:bg-red-50"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                >
                  Delete
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <span className="text-xs text-slate-400">Global</span>
        )}
      </td>
    </tr>
  );
}

function MakeModal({
  title,
  initial,
  onClose,
  onSubmit,
}: {
  title: string;
  initial?: DeviceMake;
  onClose: () => void;
  onSubmit: (values: { name: string; category?: DeviceCategory; active?: boolean }) => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState<DeviceCategory | ''>(
    (initial?.category as DeviceCategory) || 'phone',
  );
  const [pending, setPending] = useState(false);

  return (
    <ModalShell title={title} onClose={onClose}>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Name *</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </label>
      <label className="mt-3 block text-sm">
        <span className="font-medium text-slate-700">Default category</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={category}
          onChange={(e) => setCategory(e.target.value as DeviceCategory)}
        >
          {DEVICE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {categoryLabel(c)}
            </option>
          ))}
        </select>
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          disabled={!name.trim() || pending}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          onClick={() => {
            setPending(true);
            void onSubmit({
              name: name.trim(),
              ...(category ? { category } : {}),
            })
              .catch((e) => toastError(parseApiError(e).message))
              .finally(() => setPending(false));
          }}
        >
          {pending ? 'Saving…' : initial ? 'Save' : 'Create'}
        </button>
      </div>
    </ModalShell>
  );
}

function ModelModal({
  title,
  makes,
  initial,
  initialMakeId,
  allowAddAnother,
  onClose,
  onSubmit,
}: {
  title: string;
  makes: DeviceMake[];
  initial?: DeviceModel;
  initialMakeId?: string;
  allowAddAnother?: boolean;
  onClose: () => void;
  onSubmit: (
    values: {
      makeId: string;
      name: string;
      category: DeviceCategory;
      releaseYear?: number | null;
      estimatedValue?: string | null;
    },
    addAnother?: boolean,
  ) => Promise<unknown>;
}) {
  const defaultMakeId = initial?.makeId ?? initialMakeId ?? makes[0]?.id ?? '';
  const defaultMake = makes.find((m) => m.id === defaultMakeId);
  const [makeId, setMakeId] = useState(defaultMakeId);
  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState<DeviceCategory>(
    (initial?.category as DeviceCategory) ||
      (defaultMake?.category as DeviceCategory) ||
      'phone',
  );
  const [year, setYear] = useState(initial?.releaseYear?.toString() ?? '');
  const [value, setValue] = useState(initial?.estimatedValue ?? '');
  const [pending, setPending] = useState(false);

  const makeOptions = useMemo(() => makes.filter((m) => m.active !== false), [makes]);

  async function submit(addAnother: boolean) {
    if (!makeId || !name.trim()) return;
    setPending(true);
    try {
      await onSubmit(
        {
          makeId,
          name: name.trim(),
          category,
          releaseYear: year ? Number(year) : null,
          estimatedValue: value.trim() || null,
        },
        addAnother,
      );
      if (addAnother) {
        setName('');
      }
    } catch (error) {
      toastError(parseApiError(error).message);
    } finally {
      setPending(false);
    }
  }

  return (
    <ModalShell title={title} onClose={onClose}>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Make *</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={makeId}
          disabled={Boolean(initial)}
          onChange={(e) => {
            setMakeId(e.target.value);
            const m = makes.find((x) => x.id === e.target.value);
            if (m?.category) setCategory(m.category as DeviceCategory);
          }}
        >
          {makeOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-3 block text-sm">
        <span className="font-medium text-slate-700">Name *</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </label>
      <label className="mt-3 block text-sm">
        <span className="font-medium text-slate-700">Category *</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={category}
          onChange={(e) => setCategory(e.target.value as DeviceCategory)}
        >
          {DEVICE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {categoryLabel(c)}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-3 block text-sm">
        <span className="font-medium text-slate-700">Release year</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={year}
          onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="2024"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Powers age-based upsell suggestions
        </span>
      </label>
      <label className="mt-3 block text-sm">
        <span className="font-medium text-slate-700">Estimated value</span>
        <div className="mt-1 flex items-center gap-1">
          <span className="text-slate-500">$</span>
          <input
            className="w-full rounded-xl border border-slate-300 px-3 py-2"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="899"
          />
        </div>
        <span className="mt-1 block text-xs text-slate-500">
          Powers repair-cost-vs-value warnings
        </span>
      </label>
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button type="button" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600" onClick={onClose}>
          Cancel
        </button>
        {allowAddAnother && !initial ? (
          <button
            type="button"
            disabled={!name.trim() || !makeId || pending}
            className="rounded-xl border border-primary px-4 py-2 text-sm font-semibold text-primary disabled:opacity-50"
            onClick={() => void submit(true)}
          >
            Create & Add Another
          </button>
        ) : null}
        <button
          type="button"
          disabled={!name.trim() || !makeId || pending}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          onClick={() => void submit(false)}
        >
          {pending ? 'Saving…' : initial ? 'Save' : 'Create'}
        </button>
      </div>
    </ModalShell>
  );
}

function BlockedDialog({
  title,
  children,
  onCancel,
  onDeactivate,
  deactivatePending,
}: {
  title: string;
  children: ReactNode;
  onCancel: () => void;
  onDeactivate: () => void;
  deactivatePending?: boolean;
}) {
  return (
    <ModalShell title={`⚠ ${title}`} onClose={onCancel}>
      <div className="text-sm text-slate-700">{children}</div>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          disabled={deactivatePending}
          className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          onClick={onDeactivate}
        >
          {deactivatePending ? 'Deactivating…' : 'Deactivate'}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" role="dialog" aria-modal="true">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-950">{title}</h3>
          <button type="button" className="text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
